import React, { useEffect, useRef, useState, useContext } from "react";
import SearchSelectElement from "../../form/SearchSelectElement";
import InputElement from "../../form/InputElement";
import stylesRequestData from "../../../components/UnifiedTaxDeclaration/UnifiedTaxDeclarationRequestData/index.module.css";
import stylesModal from "../../../components/UnifiedTaxDeclaration/forms/formTypes/Modal.module.css";
import styles from "./CommonAddressMap.module.css";
import { post, get } from "../../../adapters/xhr/api";
import { validateRequired } from "../../../helpers/Validation";
import { getMatchingBuildings } from "../../../components/UnifiedTaxDeclaration/helpers/commonValidation";

import {
  getMapPoliceStationsByGovernorate,
  getMapGovernorates,
  getMapStreetsByPoliceStation,
  getMapCitiesByGovernorate,
  getMapDistrictsByCity,
  getMapMogawrasByDistrict,
  getSettlementsByGovID,
  getUnifiedDeclarationAllLookups,
  getStaticPoliceStations,
  getStaticGovernorate,
} from "../../../adapters/apis/lookups/lookups";
import { useParams } from "react-router-dom";
import UnifiedTaxDeclarationContext from "../../../contexts/UnifiedTaxDeclarationProvider";
import Modal from "react-modal";
import infoIconSvg from "../../../assets/img/Icon-metro-info.svg";
import InfoPopover from "../../../shared/form/InfoPopover";
import { is } from "date-fns/locale";
import {
  validateBuildingFilterSearchType,
  hasFilterSearchTypeMismatch,
} from "../../../components/UnifiedTaxDeclaration/helpers/commonValidation";

const mapConfig = {
  backgroundServiceURL: "http://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
  backgroundServiceType: "google_sat",
  apiBackendURL: process.env.REACT_APP_BASE_URL + "api",
  tokenApiBackendURL: process.env.REACT_APP_BASE_URL + "api",
};

const defaultSearchMode = 1;

const validateBuildingProfileMatch = (oldProfile, newProfile) => {
  if (!oldProfile || !newProfile) return false;

  const unitIds = oldProfile.address_ids ?? {};
  const buildingIds = newProfile.address_ids ?? {};

  const keysToCheck = [
    "district_id",
    "goveronrate_id",
    "city_id",
    "policestation_id",
  ];

  // return true only if all 4 values are equal
  return keysToCheck.every((key) => unitIds[key] === buildingIds[key]);
};

const normalizeSearchMode = (value, availableTypes) => {
  const modeValue = Number(value);
  const availableIds = (availableTypes || []).map((item) => Number(item.id));

  if (availableIds?.includes(modeValue)) return modeValue;
  return defaultSearchMode;
};

const getProfileCoordinates = (profile) =>
  profile?.geometry?.geojson?.coordinates?.[0]?.[0]?.[0];

const dispatchZoomToLatLong = (mapElement, profile) => {
  const coords = getProfileCoordinates(profile);
  if (Array.isArray(coords) && coords.length >= 2 && mapElement) {
    const [long, lat] = coords;
    mapElement.dispatchEvent(
      new CustomEvent("zoomToLatLong", {
        detail: { Lat: lat, Long: long, ZoomLevel: 20 },
      }),
    );
  }
};

const setRequestField = (prev, localMapType, changes) => {
  if (localMapType === "unit") {
    return {
      ...prev,
      unit: {
        ...prev.unit,
        ...changes,
      },
    };
  }

  return {
    ...prev,
    unit: {
      ...prev.unit,
      building: {
        ...prev.unit?.building,
        ...changes,
      },
    },
  };
};

const buildMainBuildingState = (staticProfile) => (prev) => {
  return {
    ...prev,
    unit: {
      ...prev.unit,
      buildings: [],
      building: {
        ...prev?.unit?.building,
        buildingProfile: staticProfile,
        main_building: 1,
      },
    },
  };
};

const buildAddressText = (profile) => {
  if (!profile || !profile.address) return "";
  const addressParts = [
    profile.address?.goveronrate,
    profile.address?.city,
    profile.address?.district,
    profile.address?.mogawra,
    profile.address?.street,
    profile.address?.settlement,
    profile.number,
  ].filter(Boolean);
  return addressParts.join("، ");
};

const buildFullAddressDisplay = (profile, activeUnit, options = {}) => {
  if (!profile) return "";
  const addr = profile.address || {};
  const { hideEmpty = false } = options;
  const fields = [
    ["المحافظة", addr?.goveronrate],
    ["المدينة", activeUnit?.correct_city_other || addr?.city],
    ["قسم/مركز الشرطة", addr?.policestation],
    ["الشياخة", addr?.neighborhood],
    ["الحي/المنطقة", activeUnit?.correct_district_other || addr?.district],
    ["المجاورة", activeUnit?.correct_mogawra_other || addr?.mogawra],
    ["القرية", activeUnit?.correct_village_other || addr?.village],
    ["الشارع", activeUnit?.correct_street_other || addr?.street],
    ["التجمع السكني", activeUnit?.correct_settlement_other || addr?.settlement],
    ["رقم العقار", activeUnit?.known_build_num || profile.number],
  ];

  const visibleFields = hideEmpty
    ? fields.filter(([, value]) => value && value !== "لا يوجد")
    : fields;

  return visibleFields.map(([label, value], index) => (
    <span key={label}>
      <strong>{label}:</strong> {hideEmpty ? value : value || "لا يوجد"}
      {index < visibleFields.length - 1 && "، "}
    </span>
  ));
};

function CommonAddressMap(props) {
  Modal.setAppElement("body");
  const emcRef = useRef(null);
  const emcBuildingRef = useRef(null);
  const mapInitializedRef = useRef(false);
  const { hasContainer } = useParams();

  const unifiedTaxDeclarationContext = useContext(UnifiedTaxDeclarationContext);
  const {
    declarationRequest,
    setDeclarationRequest,
    isUnitInSameBuilding,
    building,
    setBuilding,
    setIsBuildingEditMode,
    commercialUnitTypes,
    dataMismatchReasonLookup,
    DoYouWantExtraAttachmentsForRequestTypes,
    declarationFilterSearchType,
    setError,
    error_new_user,
    errorBuilding,
    setErrorBuilding,
    getLabelById,
    staticGovs,
    setStaticGovs,
    staticPoliceStations,
    setStaticPoliceStations,
    staticBuildingPoliceStations,
    setBuildingDate,
    isBuildingEditMode,
    clearAllUnitErrors,
    mapEnabled
  } = unifiedTaxDeclarationContext;

  const localMapType = props?.mapType;

  const searchUsingTypes = declarationFilterSearchType?.length
    ? declarationFilterSearchType
    : [];

  const selectedSearchModeUnit = normalizeSearchMode(
    declarationRequest?.unit?.filter_search_type_id,
    searchUsingTypes,
  );

  const selectedSearchModeBuilding =
    declarationRequest?.unit?.filter_search_type_id;

  const correctionReasons = dataMismatchReasonLookup?.length
    ? dataMismatchReasonLookup
    : [];

  const addressMatchOptions = DoYouWantExtraAttachmentsForRequestTypes?.length
    ? DoYouWantExtraAttachmentsForRequestTypes.filter((item) =>
        [1, 2].includes(Number(item?.id)),
      )
    : [];

  const [activeUnit, setActiveUnit] = useState(null);
  const [activeError, setActiveError] = useState(null);

  const [govs, setGovs] = useState([]);
  const [policeStations, setPoliceStations] = useState([]);

  const [streets, setStreets] = useState([]);

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [mogawras, setMogawras] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showHotelUnitsDeleteModal, setShowHotelUnitsDeleteModal] =
    useState(false);
  const [staticProfileForMainBuilding, setStaticProfileForMainBuilding] =
    useState(null);

  const [activeSearchMode, setActiveSearchMode] = useState(() =>
    normalizeSearchMode(
      declarationRequest?.unit?.filter_search_type_id,
      searchUsingTypes,
    ),
  );

  const generateOptions = (data) =>
    data?.map(({ id, name }) => ({ value: id.toString(), label: name }));

  useEffect(() => {
    const normalized = normalizeSearchMode(
      mapEnabled ? declarationRequest?.unit?.filter_search_type_id : 3,
      searchUsingTypes,
    );
    setActiveSearchMode(normalized);
  }, [declarationRequest?.unit?.filter_search_type_id, searchUsingTypes]);

  useEffect(() => {
    if (!declarationRequest) return;

    if (localMapType === "unit") {
      setActiveUnit(declarationRequest?.unit);
      setActiveError(error_new_user);
    } else {
      setActiveUnit(declarationRequest?.unit?.building);
      setActiveError(errorBuilding);
    }
  }, [localMapType, declarationRequest, props]);

  const getActiveMapRef = () => {
    return localMapType === "unit" ? emcRef : emcBuildingRef;
  };

  const buildFullAddressDisplayViewMode = (profile, activeUnit) => {
    if (
      !profile &&
      parseInt(declarationRequest?.unit?.filter_search_type_id) !== 3
    )
      return "";
    const addr = profile?.address || {};
    const fields = [
      ["المحافظة", addr?.goveronrate || activeUnit?.manual_governorate_name],
      [
        "المدينة",
        activeUnit?.correct_city_other || addr?.city || activeUnit?.manual_city,
      ],
      [
        "قسم/مركز الشرطة",
        addr?.policestation || activeUnit?.manual_policestation_name,
      ],
      ["الشياخة", addr?.neighborhood],
      [
        "الحي/المنطقة",
        activeUnit?.correct_district_other ||
          addr?.district ||
          activeUnit?.manual_district,
      ],
      [
        "المجاورة",
        activeUnit?.correct_mogawra_other ||
          addr?.mogawra ||
          activeUnit?.manual_mogawra,
      ],
      [
        "القرية",
        activeUnit?.correct_village_other ||
          addr?.village ||
          activeUnit?.manual_village,
      ],
      [
        "الشارع",
        activeUnit?.correct_street_other ||
          addr?.street ||
          activeUnit?.manual_street,
      ],
      [
        "التجمع السكني",
        activeUnit?.correct_settlement_other ||
          addr?.settlement ||
          activeUnit?.manual_settlement,
      ],
      [
        "رقم العقار",
        activeUnit?.known_build_num ||
          profile?.number ||
          activeUnit?.manual_known_build_num,
      ],
    ];

    return fields
      .filter(([_, value]) => value && value !== "لا يوجد") // skip empty or "لا يوجد"
      .map(([label, value], index, arr) => (
        <span key={label}>
          <strong>{label}:</strong> {value}
          {index < arr.length - 1 && "، "}
        </span>
      ));
  };

  const buildFullAddressDisplayMainBuilding = () => {
    const unit = declarationRequest?.unit || {};
    const profile = declarationRequest?.unit?.buildingProfile || {};
    const addr = declarationRequest?.unit?.buildingProfile?.address || {};
    if (!profile) return "";

    const fields = [
      ["المحافظة", addr?.goveronrate],
      ["المدينة", unit?.correct_city_other || addr?.city],
      ["قسم/مركز الشرطة", addr?.policestation],
      ["الشياخة", addr?.neighborhood],
      ["الحي/المنطقة", unit?.correct_district_other || addr?.district],
      ["المجاورة", unit?.correct_mogawra_other || addr?.mogawra],
      ["القرية", unit?.correct_village_other || addr?.village],
      ["الشارع", unit?.correct_street_other || addr?.street],
      ["التجمع السكني", unit?.correct_settlement_other || addr?.settlement],
      ["رقم العقار", unit?.known_build_num || profile.number],
    ];

    return fields
      .filter(([_, value]) => value && value !== "لا يوجد") // skip empty or "لا يوجد"
      .map(([label, value], index, arr) => (
        <span key={label}>
          <strong>{label}:</strong> {value}
          {index < arr.length - 1 && "، "}
        </span>
      ));
  };
  const mapZoomToLayer = (mapLayerCode) => {
    const targetRef = getActiveMapRef();
    if (
      !targetRef?.current
      // || !mapInitializedRef.current
    )
      return;
    const event = new CustomEvent("zoomToLayer", {
      detail: { MapLayerCode: mapLayerCode },
    });
    targetRef.current.dispatchEvent(event);
  };

  const mapZoomToFeature = (mapLayerCode, featureId) => {
    const targetRef = getActiveMapRef();
    if (
      !targetRef?.current
      // || !mapInitializedRef.current
    )
      return;
    if (!featureId && featureId !== 0) return;

    const parsed = Number(featureId);
    if (!Number.isFinite(parsed) || parsed <= 0) return;

    const event = new CustomEvent("zoomToFeature", {
      detail: { MapLayerCode: mapLayerCode, FeatureID: parsed },
    });
    targetRef.current.dispatchEvent(event);
  };

  useEffect(() => {
    const unitProfile = declarationRequest?.unit?.buildingProfile;
    const buildingProfile = declarationRequest?.unit?.building?.buildingProfile;

    // Zoom to unit profile if present
    if (unitProfile) {
      const targetRef = emcRef.current;
      if (targetRef && mapInitializedRef.current) {
        dispatchZoomToLatLong(targetRef, unitProfile);
      }
    }

    // Zoom to building profile if present
    if (buildingProfile) {
      const targetRef = emcBuildingRef.current;
      if (targetRef && mapInitializedRef.current) {
        dispatchZoomToLatLong(targetRef, buildingProfile);
      }
    }
    // }
  }, [
    localMapType,
    declarationRequest,
    declarationRequest?.unit?.buildingProfile,
    declarationRequest?.unit?.building?.buildingProfile,
  ]);

  const isBuildingHasDifferentSearchType = validateBuildingFilterSearchType(
    declarationRequest,
    declarationRequest?.unit?.filter_search_type_id,
  );

  const areAllBuildingsTypeOneOrTwo = (declarationRequest) => {
    const buildings = getMatchingBuildings(declarationRequest) || [];

    // return true only if every building has type 1 or 2
    return buildings.every((b) => {
      const type = parseInt(b?.filter_search_type);
      return type === 1 || type === 2;
    });
  };

  // working version without updating the main building
  useEffect(() => {
    const mapElement =
      localMapType === "unit" ? emcRef?.current : emcBuildingRef?.current;

    const onDoubleClick = () => {
      const listener = (e) => {
        const transformedProfile = transformObject(e.detail);
        const buildings = getMatchingBuildings(declarationRequest);
        const isMatching = validateBuildingProfileMatch(
          declarationRequest?.unit?.buildingProfile,
          transformedProfile,
        );
        setStaticProfileForMainBuilding(transformedProfile);

        if (
          localMapType === "unit" &&
          !declarationRequest?.viewMode &&
          Object.keys(transformedProfile?.address)?.length > 0 &&
          Object.keys(transformedProfile?.address_ids)?.length > 0
        ) {
          if (isEmptyProfile(transformedProfile)) {
            props?.setUnitMapError(
              "هذا العقار غير متاح حالياً، لحين تحديث بيانات الخريطة برجاء اختيار أقرب عقار لسهولة تحديد الموقع",
            );
            // if (!buildings || buildings?.length === 0) {
            //   setDeclarationRequest((prev) => ({
            //     ...prev,
            //     unit: {
            //       ...prev?.unit,
            //       building: {
            //         ...prev?.unit?.building,
            //         buildingProfile: null,
            //       },
            //     },
            //   }));

            //   setBuilding((prev) => ({
            //     ...prev,
            //     buildingProfile: null,
            //     main_building: 1,
            //   }));
            // }
            return;
          } else {
            props?.setUnitMapError("");
          }

          if (props?.numberOfBuildings > 1 && areAllBuildingsTypeOneOrTwo()) {
            if (!isMatching) {
              setShowModal(true);
              return;
            }
          }

          if (
            isMatching &&
            parseInt(declarationRequest?.unit?.property_type_id) === 6 &&
            props?.numberOfBuildings >= 1 &&
            areAllBuildingsTypeOneOrTwo()
          ) {
            setShowHotelUnitsDeleteModal(true);
            return;
          }

          handleLogicOfChangingUnitBuildingProfile(
            buildings,
            transformedProfile,
          );

          return;
        }

        // // --- BUILDING MAP ---
        if (
          localMapType === "building" &&
          !declarationRequest?.viewMode &&
          Object.keys(transformedProfile?.address)?.length > 0 &&
          Object.keys(transformedProfile?.address_ids)?.length > 0
        ) {
          if (props?.isMainBuilding) {
            return;
          }

          if (!buildings || buildings?.length === 0) {
            return;
          }

          if (
            parseInt(
              declarationRequest?.unit?.building?.buildingProfile?.renin,
            ) !== parseInt(transformedProfile?.renin)
          ) {
            setDeclarationRequest((prev) =>
              setRequestField(prev, "building", {
                is_map_address_matching: null,
                mismatch_reason_id: null,
                correct_city_other: null,
                correct_district_other: null,
                correct_mogawra_other: null,
                correct_village_other: null,
                correct_street_other: null,
                correct_settlement_other: null,
                known_build_num: null,
              }),
            );
          }

          if (isEmptyProfile(transformedProfile)) {
            props?.setUnitBuildingMapError(
              "هذا العقار غير متاح حالياً، لحين تحديث بيانات الخريطة برجاء اختيار أقرب عقار لسهولة تحديد الموقع",
            );
            setDeclarationRequest((prev) => ({
              ...prev,
              unit: {
                ...prev?.unit,
                building: {
                  ...prev?.unit?.building,
                  buildingProfile: null,
                },
              },
            }));

            setBuilding((prev) => ({
              ...prev,
              buildingProfile: null,
              main_building: 0,
            }));

            return;
          } else {
            props?.setUnitBuildingMapError("");
            setDeclarationRequest((prev) => ({
              ...prev,
              unit: {
                ...prev?.unit,
                building: {
                  ...prev?.unit?.building,
                  buildingProfile: transformedProfile,
                },
              },
            }));

            setBuilding((prev) => ({
              ...prev,
              buildingProfile: transformedProfile,
              main_building: 0,
            }));

            // Zoom for building map
            dispatchZoomToLatLong(emcBuildingRef.current, transformedProfile);
          }
        }
      };

      mapElement.addEventListener("IdentifiedFeatureData", listener, {
        once: true,
      });
    };

    mapElement?.addEventListener("dblclick", onDoubleClick);
    return () => mapElement?.removeEventListener("dblclick", onDoubleClick);
  }, [localMapType, declarationRequest, activeSearchMode]);

  const handleLogicOfChangingUnitBuildingProfile = (
    buildings,
    transformedProfile,
  ) => {
    if (
      parseInt(declarationRequest?.unit?.buildingProfile?.renin) !==
      parseInt(transformedProfile?.renin)
    ) {
      setDeclarationRequest((prev) =>
        setRequestField(prev, "unit", {
          is_map_address_matching: null,
          mismatch_reason_id: null,
          correct_city_other: null,
          correct_district_other: null,
          correct_mogawra_other: null,
          correct_village_other: null,
          correct_street_other: null,
          correct_settlement_other: null,
          known_build_num: null,
        }),
      );
    }
    setDeclarationRequest((prev) => ({
      ...prev,
      unit: {
        ...prev?.unit,
        buildingProfile: transformedProfile,
      },
      // hotelUnits: [],
    }));
    // Zoom for unit map
    dispatchZoomToLatLong(emcRef.current, transformedProfile);
    if (!buildings || buildings?.length === 0) {
      setDeclarationRequest((prev) => ({
        ...prev,
        unit: {
          ...prev?.unit,
          building: {
            ...prev?.unit?.building,
            buildingProfile: transformedProfile,
            main_building: 1,
          },
          hotelUnits: [],
        },
      }));

      setBuilding((prev) => ({
        ...prev,
        buildingProfile: transformedProfile,
        main_building: 1,
      }));

      // Zoom for building map
      dispatchZoomToLatLong(emcBuildingRef.current, transformedProfile);
    }

    if (
      buildings?.length >= 1 &&
      !hasFilterSearchTypeMismatch(declarationRequest)
    ) {
      setDeclarationRequest((prev) => {
        // guard if no unit exists
        if (!prev?.unit) return prev;

        let updatedBuildings = prev?.unit?.buildings || [];

        // Case 1: main building exists inside buildings → update it
        if (updatedBuildings?.length) {
          updatedBuildings = updatedBuildings?.map((b) =>
            parseInt(b.main_building) === 1
              ? {
                  ...b,
                  buildingProfile: transformedProfile,
                  main_building: 1,
                  is_map_address_matching: null,
                  mismatch_reason_id: null,
                  correct_city_other: null,
                  correct_district_other: null,
                  correct_mogawra_other: null,
                  correct_village_other: null,
                  correct_street_other: null,
                  correct_settlement_other: null,
                  known_build_num: null,
                }
              : b,
          );
        }

        // Case 2: main building exists only in unit.building → update it
        let updatedSingleBuilding = prev?.unit?.building;
        if (parseInt(updatedSingleBuilding?.main_building) === 1) {
          updatedSingleBuilding = {
            ...updatedSingleBuilding,
            buildingProfile: transformedProfile,
            main_building: 1,
            is_map_address_matching: null,
            mismatch_reason_id: null,
            correct_city_other: null,
            correct_district_other: null,
            correct_mogawra_other: null,
            correct_village_other: null,
            correct_street_other: null,
            correct_settlement_other: null,
            known_build_num: null,
          };
        }

        return {
          ...prev,
          unit: {
            ...prev.unit,
            buildings: updatedBuildings, // keep others as-is
            building: updatedSingleBuilding, // keep main building here if that's where it lives
            hotelUnits: [],
          },
        };
      });

      setIsBuildingEditMode(false);
      setBuilding(null);

      if (!declarationRequest?.isUnitEditMode) {
        if (parseInt(declarationRequest?.unit?.property_type_id) === 6) {
          setDeclarationRequest((prev) => ({
            ...prev,
            unit: {
              ...prev.unit,
              hotelUnits: [],
            },
          }));
        }
      }
    }
  };

  const transformObject = (obj) => {
    const address = obj?.address || {}; // ✅ ensure it's always an object
    const newAddress = {};
    const addressIds = {};

    for (const [key, value] of Object.entries(address)) {
      if (value === "|") {
        newAddress[key] = null;
        addressIds[`${key}_id`] = null;
      } else if (typeof value === "string" && value.includes("|")) {
        const [text, id] = value.split("|");
        newAddress[key] = text || null;
        addressIds[`${key}_id`] = id || null;
      } else {
        // For values without "|", keep them in address and set ids to null
        newAddress[key] = value;
        addressIds[`${key}_id`] = null;
      }
    }

    return {
      ...obj,
      address: newAddress,
      address_ids: addressIds,
    };
  };

  // Utility function to check if profile is empty
  const isEmptyProfile = (profile) => {
    if (!profile) return true;

    // Check top-level fields
    const topLevelEmpty =
      !profile.renin &&
      !profile.number &&
      !profile.name &&
      !profile.area_m2 &&
      !profile.type;

    // Check address fields: all are either null or "|"
    const address = profile.address || {};
    const addressEmpty = Object.values(address).every(
      (val) => val === null || val === "|",
    );

    // Check geometry
    const geometryEmpty = !profile.geometry?.geojson;

    return topLevelEmpty && addressEmpty && geometryEmpty;
  };

  useEffect(() => {
    renderGovernorateAndPoliceManualFields();
  }, [localMapType]);

  useEffect(() => {
    if (
      getMatchingBuildings(declarationRequest)?.length === 0 ||
      getMatchingBuildings(declarationRequest) === undefined
    ) {
      setBuilding((prev) => ({
        ...prev,
        main_building: 1,
      }));
      if (parseInt(declarationRequest?.unit?.filter_search_type_id) !== 3) {
        setDeclarationRequest((prev) => ({
          ...prev,
          unit: {
            ...prev?.unit,
            building: {
              ...prev?.unit?.building,
              buildingProfile: declarationRequest?.unit?.buildingProfile,
              main_building: 1,
            },
          },
        }));

        // setBuilding((prev) => ({
        //   ...prev,
        //   buildingProfile: declarationRequest?.unit?.buildingProfile,
        //   main_building: 1,
        // }));
      }
    }
  }, [declarationRequest?.unit?.filter_search_type_id]);

  useEffect(() => {
    if (
      parseInt(declarationRequest?.unit?.filter_search_type_id) === 3 &&
      getMatchingBuildings(declarationRequest)?.length > 0
    ) {
      const buildings = getMatchingBuildings(declarationRequest) || [];
      const updatedBuildings = buildings.map((b) =>
        parseInt(b?.main_building) === 1
          ? {
              ...b,
              manual_governorate_id:
                declarationRequest?.unit?.manual_governorate_id,
              manual_policestation_id:
                declarationRequest?.unit?.manual_policestation_id,
              manual_policestation_name: getLabelById(
                declarationRequest?.unit?.manual_policestation_id,
                staticPoliceStations,
              ),
              manual_city: declarationRequest?.unit?.manual_city,
              manual_district: declarationRequest?.unit?.manual_district,
              manual_mogawra: declarationRequest?.unit?.manual_mogawra,
              manual_village: declarationRequest?.unit?.manual_village,
              manual_street: declarationRequest?.unit?.manual_street,
              manual_settlement: declarationRequest?.unit?.manual_settlement,
              manual_known_build_num:
                declarationRequest?.unit?.manual_known_build_num,
              manual_renin: declarationRequest?.unit?.manual_renin,
            }
          : {
              ...b,
              manual_governorate_id:
                declarationRequest?.unit?.manual_governorate_id,
              manual_policestation_id:
                declarationRequest?.unit?.manual_policestation_id,
              manual_policestation_name: getLabelById(
                declarationRequest?.unit?.manual_policestation_id,
                staticPoliceStations,
              ),
              manual_city: declarationRequest?.unit?.manual_city,
              manual_district: declarationRequest?.unit?.manual_district,
              manual_mogawra: declarationRequest?.unit?.manual_mogawra,
              manual_village: declarationRequest?.unit?.manual_village,
              manual_settlement: declarationRequest?.unit?.manual_settlement,
            },
      );

      setDeclarationRequest((prev) => ({
        ...prev,
        unit: {
          ...prev.unit,
          buildings: updatedBuildings,
        },
      }));
    }
  }, [
    declarationRequest?.unit?.manual_governorate_id,
    declarationRequest?.unit?.manual_policestation_id,
    declarationRequest?.unit?.manual_city,
    declarationRequest?.unit?.manual_district,
    declarationRequest?.unit?.manual_mogawra,
    declarationRequest?.unit?.manual_village,
    declarationRequest?.unit?.manual_street,
    declarationRequest?.unit?.manual_settlement,
    declarationRequest?.unit?.manual_known_build_num,
    declarationRequest?.unit?.manual_renin,
  ]);

  const setStateOfMainBuildingInSingleBuildingAndBuildingsArray = () => {
    setBuilding((prev) => ({
      ...prev,
      main_building: 1,
      manual_city: declarationRequest?.unit?.manual_city,
      manual_district: declarationRequest?.unit?.manual_district,
      manual_mogawra: declarationRequest?.unit?.manual_mogawra,
      manual_village: declarationRequest?.unit?.manual_village,
      manual_street: declarationRequest?.unit?.manual_street,
      manual_settlement: declarationRequest?.unit?.manual_settlement,
      manual_known_build_num: declarationRequest?.unit?.manual_known_build_num,
      manual_renin: declarationRequest?.unit?.manual_renin,
    }));

    setDeclarationRequest((prev) =>
      setRequestField(prev, "building", {
        manual_city: declarationRequest?.unit?.manual_city,
        manual_district: declarationRequest?.unit?.manual_district,
        manual_mogawra: declarationRequest?.unit?.manual_mogawra,
        manual_village: declarationRequest?.unit?.manual_village,
        manual_street: declarationRequest?.unit?.manual_street,
        manual_settlement: declarationRequest?.unit?.manual_settlement,
        manual_known_build_num:
          declarationRequest?.unit?.manual_known_build_num,
        manual_renin: declarationRequest?.unit?.manual_renin,
      }),
    );
  };

  useEffect(() => {
    if (
      parseInt(declarationRequest?.unit?.filter_search_type_id) !== 3 &&
      [5, 6, 7, 8, 9].includes(
        parseInt(declarationRequest?.unit?.property_type_id),
      )
    ) {
      if (
        getMatchingBuildings(declarationRequest)?.length === 0 ||
        getMatchingBuildings(declarationRequest) === undefined
      ) {
        setBuilding((prev) => ({
          ...prev,
          main_building: 1,
        }));
      }
    }
  }, [declarationRequest?.unit?.filter_search_type_id]);

  useEffect(() => {
    if (parseInt(declarationRequest?.unit?.filter_search_type_id) === 3) {
      const buildings = getMatchingBuildings(declarationRequest) || [];

      if (!buildings || buildings.length === 0) {
        // Case 1: no buildings → create new main building
        setStateOfMainBuildingInSingleBuildingAndBuildingsArray();
      }
    }
  }, [
    activeUnit?.filter_search_type_id,
    activeUnit?.manual_city,
    activeUnit?.manual_district,
    activeUnit?.manual_mogawra,
    activeUnit?.manual_village,
    activeUnit?.manual_street,
    activeUnit?.manual_settlement,
    activeUnit?.manual_known_build_num,
    activeUnit?.manual_renin,
  ]);

  const renderGovernorateAndPoliceManualFields = () => {
    const isManual =
      localMapType === "unit" ||
      (localMapType === "building" && parseInt(building?.main_building) === 1);
    if (isManual) {
      return (
        <>
          <div className="col-lg-4 col-sm-6">
            <SearchSelectElement
              mb4="mb-2"
              labelHtmlFor="manual_governorate_id"
              labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
              labelText="المحافظة"
              selectClassName={`${stylesRequestData["form-select"]} form-select`}
              selectID="manual_governorate_id"
              inputId="manual_governorate_id_input"
              name="manual_governorate_id"
              options={generateOptions(staticGovs)}
              default="أختار المحافظة.."
              pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
              pText={error_new_user.manual_governorate_id}
              value={parseInt(declarationRequest?.unit?.manual_governorate_id)}
              onChange={handleChangeStaticGov}
              isClearable={true}
              disabled={
                isViewMode ||
                (localMapType === "building" &&
                  parseInt(building?.main_building) === 1)
              }
            />
          </div>
          <div className="col-lg-4 col-sm-6">
            <SearchSelectElement
              mb4="mb-2"
              labelHtmlFor="manual_policestation_id"
              labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
              labelText="قسم/مركز الشرطة"
              selectClassName={`${stylesRequestData["form-select"]} form-select`}
              selectID="manual_policestation_id"
              inputId="manual_policestation_id_input"
              name="manual_policestation_id"
              options={
                declarationRequest?.unit?.manual_governorate_id
                  ? generateOptions(staticPoliceStations)
                  : []
              }
              default="أختار مركز/ قسم شرطة .."
              pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
              pText={error_new_user.manual_policestation_id}
              value={parseInt(
                declarationRequest?.unit?.manual_policestation_id,
              )}
              onChange={handleChangeStaticPoliceStation}
              isClearable={true}
              disabled={
                isViewMode ||
                (localMapType === "building" &&
                  parseInt(building?.main_building) === 1)
              }
            />
          </div>
        </>
      );
    }

    // building_manual_* branch
    return (
      <>
        <div className="col-lg-4 col-sm-6">
          <SearchSelectElement
            mb4="mb-2"
            labelHtmlFor="building_manual_governorate_id"
            labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
            labelText="المحافظة"
            selectClassName={`${stylesRequestData["form-select"]} form-select`}
            selectID="building_manual_governorate_id"
            inputId="building_manual_governorate_id_input"
            name="building_manual_governorate_id"
            options={generateOptions(staticGovs)}
            default="أختار المحافظة.."
            pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
            pText={error_new_user.building_manual_governorate_id}
            value={parseInt(declarationRequest?.unit?.manual_governorate_id)}
            onChange={handleChangeStaticGov}
            isClearable={true}
            disabled={true}
          />
        </div>
        <div className="col-lg-4 col-sm-6">
          <SearchSelectElement
            mb4="mb-2"
            labelHtmlFor="building_manual_policestation_id"
            labelClassName={`form-label required ${stylesRequestData["form-label"]}`}
            labelText="قسم/مركز الشرطة"
            selectClassName={`${stylesRequestData["form-select"]} form-select`}
            selectID="building_manual_policestation_id"
            inputId="building_manual_policestation_id_input"
            name="building_manual_policestation_id"
            options={generateOptions(staticPoliceStations)}
            default="أختار مركز/ قسم شرطة .."
            pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
            pText={error_new_user.building_manual_policestation_id}
            value={parseInt(declarationRequest?.unit?.manual_policestation_id)}
            onChange={handleChangeStaticPoliceStation}
            isClearable={true}
            disabled={true}
          />
        </div>
      </>
    );
  };

  const renderBuildKnownManualField = () => {
    const isManual =
      localMapType === "unit" ||
      (localMapType === "building" && parseInt(building?.main_building) === 1);
    if (isManual) {
      return (
        <>
          <div className="col-12 col-lg-4">
            <InputElement
              mb4="mb-4"
              labelHtmlFor="manual_known_build_num"
              labelClassName={`required ${stylesRequestData["form-label"]} form-label`}
              labelText="رقم العقار/المبنى كما هو متعارف عليه"
              inputType="text"
              maxLength={100}
              inputClassName={`${stylesRequestData["form-control"]} form-control`}
              inputID="manual_known_build_num_input"
              name="manual_known_build_num"
              pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
              value={activeUnit?.manual_known_build_num || ""}
              allowedPattern={
                /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9,.\-،/\s]+$/
              }
              onChange={handleChangeDeclerationUnit}
              pText={error_new_user.manual_known_build_num}
              disabled={
                isViewMode ||
                (localMapType === "building" &&
                  parseInt(building?.main_building) === 1)
              }
            />
          </div>
        </>
      );
    }

    // building_manual_* branch
    return (
      <>
        <div className="col-12 col-lg-4">
          <InputElement
            mb4="mb-4"
            labelHtmlFor="building_manual_known_build_num"
            labelClassName={`required ${stylesRequestData["form-label"]} form-label`}
            labelText="رقم العقار/المبنى المتعارف عليه"
            inputType="text"
            maxLength={100}
            inputClassName={`${stylesRequestData["form-control"]} form-control`}
            inputID="building_manual_known_build_num_input"
            name="building_manual_known_build_num"
            pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
            value={
              declarationRequest?.unit?.building
                ?.building_manual_known_build_num || ""
            }
            allowedPattern={
              /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9\-,.،\/\\\s]+$/
            }
            onChange={handleChangeDeclerationUnit}
            pText={error_new_user.building_manual_known_build_num}
            disabled={isViewMode}
          />
        </div>
      </>
    );
  };

  // initialize
  useEffect(() => {
    if ([1, 2].includes(activeSearchMode)) {
      const mapElement = getActiveMapRef()?.current;
      const profile =
        localMapType === "unit"
          ? declarationRequest?.unit?.buildingProfile
          : declarationRequest?.unit?.building?.buildingProfile;

      const loadMapAndGovernorates = async () => {
        if (
          mapElement
          // && !mapInitializedRef.current
        ) {
          mapElement.dispatchEvent(
            new CustomEvent("initializeMap", {
              detail: {
                BackgroundServiceURL: mapConfig.backgroundServiceURL,
                BackgroundServiceType: mapConfig.backgroundServiceType,
                APIBackendURL: mapConfig.apiBackendURL,
                TokenApiBackendURL: mapConfig.tokenApiBackendURL,
              },
            }),
          );

          // mapInitializedRef.current = true;
          // if (!profile) {
          //   mapZoomToLayer(1);
          // }
        }

        try {
          const response = await getMapGovernorates();
          setGovs(response?.data?.data || []);
        } catch (error) {
          setGovs([]);
          console.error("Failed to load map governorates", error);
        }
      };

      loadMapAndGovernorates();
    }
  }, [activeSearchMode]);

  useEffect(() => {
    const fetchGovernorates = async () => {
      if ([3].includes(activeSearchMode)) {
        try {
          const response = await getStaticGovernorate();
          setGovs(response?.data?.data || []);
        } catch (error) {
          setGovs([]);
          console.error("Failed to load map governorates", error);
        }
      }
    };

    fetchGovernorates();
  }, [activeSearchMode]);

  useEffect(() => {
    if (!hasFilterSearchTypeMismatch(declarationRequest)) {
      setDeclarationRequest((prev) => {
        if (!getMatchingBuildings(declarationRequest)) return prev;

        const updatedBuildings = getMatchingBuildings(declarationRequest)?.map(
          (b) =>
            parseInt(b?.main_building) === 1
              ? {
                  ...b,
                  is_map_address_matching: prev?.unit?.is_map_address_matching,
                  mismatch_reason_id: prev?.unit?.mismatch_reason_id,
                  correct_city_other: prev?.unit?.correct_city_other,
                  correct_district_other: prev?.unit?.correct_district_other,
                  correct_mogawra_other: prev?.unit?.correct_mogawra_other,
                  correct_settlement_other:
                    prev?.unit?.correct_settlement_other,
                  correct_street_other: prev?.unit?.correct_street_other,
                  correct_village_other: prev?.unit?.correct_village_other,
                  known_build_num: prev?.unit?.known_build_num,
                  buildingProfile: prev?.unit?.buildingProfile,
                }
              : b,
        );

        return {
          ...prev,
          unit: {
            ...prev.unit,
            buildings: updatedBuildings,
          },
        };
      });

      if (parseInt(building?.main_building) === 1) {
        setBuilding((prev) => ({
          ...prev,
          is_map_address_matching:
            declarationRequest?.unit?.is_map_address_matching,
          mismatch_reason_id: declarationRequest?.unit?.mismatch_reason_id,
          correct_city_other: declarationRequest?.unit?.correct_city_other,
          correct_district_other:
            declarationRequest?.unit?.correct_district_other,
          correct_mogawra_other:
            declarationRequest?.unit?.correct_mogawra_other,
          correct_settlement_other:
            declarationRequest?.unit?.correct_settlement_other,
          correct_street_other: declarationRequest?.unit?.correct_street_other,
          correct_village_other:
            declarationRequest?.unit?.correct_village_other,
          known_build_num: declarationRequest?.unit?.known_build_num,
          buildingProfile: declarationRequest?.unit?.buildingProfile,
        }));
      }
    }
  }, [
    declarationRequest?.unit?.is_map_address_matching,
    declarationRequest?.unit?.mismatch_reason_id,
    declarationRequest?.unit?.buildingProfile,
    declarationRequest?.unit?.correct_city_other,
    declarationRequest?.unit?.correct_district_other,
    declarationRequest?.unit?.correct_mogawra_other,
    declarationRequest?.unit?.correct_settlement_other,
    declarationRequest?.unit?.correct_street_other,
    declarationRequest?.unit?.correct_village_other,
    declarationRequest?.unit?.known_build_num,
  ]);

  const getManualPoliceStations = async () => {
    try {
      const response = await getStaticPoliceStations(
        activeUnit?.manual_governorate_id,
      );
      setStaticPoliceStations(response?.data?.data || []);
    } catch (error) {
      console.error("Failed to get decelration data", error);
    }
  };

  useEffect(() => {
    if (
      activeUnit?.manual_governorate_id !== null &&
      activeUnit?.manual_governorate_id !== undefined &&
      activeUnit?.manual_governorate_id !== ""
    ) {
      getManualPoliceStations();
    }
  }, [activeUnit?.manual_governorate_id]);

  const handleChangeDeclerationUnit = (pc, c, key, value) => {
    setError(key, "");
    let updatedBuilding;
    setDeclarationRequest((prev) =>
      setRequestField(prev, localMapType, { [key]: value }),
    );
    if (localMapType === "building") {
      updatedBuilding = {
        ...building,
        [key]: value,
      };
      setBuilding(updatedBuilding);
    }

    if (key === "is_map_address_matching") {
      if (parseInt(value) !== 2) {
        setDeclarationRequest((prev) =>
          setRequestField(prev, localMapType, {
            mismatch_reason_id: null,
            correct_city_other: null,
            correct_district_other: null,
            correct_mogawra_other: null,
            correct_village_other: null,
            correct_street_other: null,
            correct_settlement_other: null,
            known_build_num: null,
          }),
        );
        setBuilding((prev) => ({
          ...prev,
          mismatch_reason_id: null,
          correct_city_other: null,
          correct_district_other: null,
          correct_mogawra_other: null,
          correct_village_other: null,
          correct_street_other: null,
          correct_settlement_other: null,
          known_build_num: null,
        }));
      } else {
        setDeclarationRequest((prev) =>
          setRequestField(prev, localMapType, {
            mismatch_reason_id: null,
            known_build_num: null,
            correct_city_other: getLabelById(activeUnit?.gg_city_id, cities),
            correct_district_other: getLabelById(
              activeUnit?.gg_district_id,
              districts,
            ),
            correct_mogawra_other: getLabelById(
              activeUnit?.gg_mogawra_id,
              mogawras,
            ),
            correct_village_other: null,
            correct_street_other: getLabelById(
              activeUnit?.gg_street_id,
              streets,
            ),
            correct_settlement_other: getLabelById(
              activeUnit?.gg_settlement_id,
              settlements,
            ),
          }),
        );
        setBuilding((prev) => ({
          ...prev,
          mismatch_reason_id: null,
          correct_city_other: getLabelById(activeUnit?.gg_city_id, cities),
          correct_district_other: getLabelById(
            activeUnit?.gg_district_id,
            districts,
          ),
          correct_mogawra_other: getLabelById(
            activeUnit?.gg_mogawra_id,
            mogawras,
          ),
          correct_village_other: null,
          correct_street_other: getLabelById(activeUnit?.gg_street_id, streets),
          correct_settlement_other: getLabelById(
            activeUnit?.gg_settlement_id,
            settlements,
          ),
        }));
      }
    }

    window.parent.postMessage(
      {
        type: "DeclarationUnitChange",
        payload: { pc, c, key, value, localMapType },
      },
      "*",
    );
  };

  const resetFromPoliceStation = () => {
    setStreets([]);
    setDeclarationRequest((prev) =>
      setRequestField(prev, localMapType, { gg_street_id: null }),
    );
  };

  const resetFromGovernorate = () => {
    setPoliceStations([]);
    setStreets([]);
    setCities([]);
    setDistricts([]);
    setMogawras([]);
    setSettlements([]);
    setDeclarationRequest((prev) =>
      setRequestField(prev, localMapType, {
        gg_police_station_id: null,
        gg_street_id: null,
        gg_city_id: null,
        gg_district_id: null,
        gg_mogawra_id: null,
        gg_settlement_id: null,
        gg_village_id: null,
      }),
    );
  };

  const resetFromCity = () => {
    setDistricts([]);
    setMogawras([]);
    setDeclarationRequest((prev) =>
      setRequestField(prev, localMapType, {
        gg_district_id: null,
        gg_mogawra_id: null,
      }),
    );
  };

  const resetFromDistrict = () => {
    setMogawras([]);
    setDeclarationRequest((prev) =>
      setRequestField(prev, localMapType, { gg_mogawra_id: null }),
    );
  };

  const handleChangeStaticGov = async (pc, c, key, value) => {
    try {
      handleChangeDeclerationUnit(pc, c, key, value);

      //TODO in main building
      if (localMapType === "unit") {
        setDeclarationRequest((prev) => ({
          ...prev,
          unit: {
            ...prev?.unit,
            manual_policestation_id: null,
            manual_policestation_name: null,
            building: {
              ...prev?.unit?.building,
              manual_governorate_id: value,
              manual_policestation_id: null,
              manual_policestation_name: null,
            },
          },
        }));
        if (value !== null && value !== undefined && value !== "") {
          const response = await getStaticPoliceStations(value);
          setStaticPoliceStations(response?.data?.data || []);
        }
      }
    } catch (error) {
      console.error("Failed to get decelration data", error);
    }
  };

  const handleChangeStaticPoliceStation = async (pc, c, key, value) => {
    handleChangeDeclerationUnit(pc, c, key, value);
    if (parseInt(building?.main_building) === 1 && localMapType === "unit") {
      setDeclarationRequest((prev) => ({
        ...prev,
        unit: {
          ...prev?.unit,
          building: {
            ...prev?.unit?.building,
            manual_policestation_id: value,
            manual_policestation_name: getLabelById(
              value,
              staticPoliceStations,
            ),
          },
        },
      }));
    }
    if (localMapType === "building") {
      setBuilding({
        ...building,
        manual_policestation_id: value,
        manual_policestation_name: getLabelById(value, staticPoliceStations),
      });
    }
  };

  const handleChangeGov = async (pc, c, key, value) => {
    resetFromGovernorate();
    handleChangeDeclerationUnit(pc, c, key, value);

    if (!value) return;

    try {
      if (activeSearchMode === 1) {
        const response = await getMapPoliceStationsByGovernorate(value);
        setPoliceStations(response?.data?.data || []);
      }
      if (activeSearchMode === 2) {
        const response = await getMapCitiesByGovernorate(value);
        setCities(response?.data?.data || []);
      }

      const settlementResponse = await getSettlementsByGovID(value);
      setSettlements(settlementResponse?.data?.data || []);

      mapZoomToFeature(1, value);
    } catch (error) {
      setCities([]);
      setPoliceStations([]);
      setSettlements([]);
      console.error("Failed to load map governorate dependencies", error);
    }
  };

  const handleChangePoliceStation = async (pc, c, key, value) => {
    resetFromPoliceStation();
    handleChangeDeclerationUnit(pc, c, key, value);

    if (!value) return;

    try {
      const response = await getMapStreetsByPoliceStation(value);
      setStreets(response?.data?.data || []);
      mapZoomToFeature(2, value);
    } catch (error) {
      setStreets([]);
      console.error("Failed to load map streets or streets", error);
    }
  };

  const handleChangeStreet = (pc, c, key, value) => {
    handleChangeDeclerationUnit(pc, c, key, value);
    handleChangeDeclerationUnit(
      pc,
      c,
      "correct_street_other",
      getLabelById(value, streets),
    );
    mapZoomToFeature(4, value);
  };

  const handleChangeSettlement = (pc, c, key, value) => {
    handleChangeDeclerationUnit(pc, c, key, value);
    handleChangeDeclerationUnit(
      pc,
      c,
      "correct_settlement_other",
      getLabelById(value, settlements),
    );
    mapZoomToFeature(8, value);
  };

  const handleChangeCity = async (pc, c, key, value) => {
    resetFromCity();
    handleChangeDeclerationUnit(pc, c, key, value);
    handleChangeDeclerationUnit(
      pc,
      c,
      "correct_city_other",
      getLabelById(value, cities),
    );

    if (!value) return;

    try {
      const response = await getMapDistrictsByCity(value);
      setDistricts(response?.data?.data || []);
      mapZoomToFeature(5, value);
    } catch (error) {
      setDistricts([]);
      console.error("Failed to load map districts", error);
    }
  };

  const handleChangeDistrict = async (pc, c, key, value) => {
    resetFromDistrict();
    handleChangeDeclerationUnit(pc, c, key, value);
    handleChangeDeclerationUnit(
      pc,
      c,
      "correct_district_other",
      getLabelById(value, districts),
    );

    if (!value) return;

    try {
      const response = await getMapMogawrasByDistrict(value);
      setMogawras(response?.data?.data || []);
      mapZoomToFeature(6, value);
    } catch (error) {
      setMogawras([]);
      console.error("Failed to load map mogawras", error);
    }
  };

  const handleChangeMogawra = async (pc, c, key, value) => {
    handleChangeDeclerationUnit(pc, c, key, value);
    handleChangeDeclerationUnit(
      pc,
      c,
      "correct_mogawra_other",
      getLabelById(value, mogawras),
    );
    mapZoomToFeature(7, value);
  };

  const activeBuildingProfile =
    localMapType === "unit"
      ? declarationRequest?.unit?.buildingProfile
      : declarationRequest?.unit?.building?.buildingProfile;
  const isAddressMismatch = parseInt(activeUnit?.is_map_address_matching) === 2;

  const hasSelectedMapProfile = !!activeBuildingProfile;
  const shouldShowManualCityLookup =
    isAddressMismatch &&
    activeUnit?.mismatch_reason_id &&
    hasSelectedMapProfile &&
    (!activeBuildingProfile?.address?.city ||
      activeBuildingProfile?.address?.city === "لا يوجد");

  const isViewMode =
    declarationRequest?.viewMode || declarationRequest?.isUnitViewMode;

  const correct_astrick_colour = (() => {
    if (activeUnit?.correct_astrick_colour)
      return activeUnit.correct_astrick_colour;
    if (declarationRequest?.unit?.correct_astrick_colour)
      return declarationRequest.unit.correct_astrick_colour;

    const renin = activeBuildingProfile?.renin || activeUnit?.manual_renin;
    if (renin) {
      const ALL_UNIT_LIST_KEYS = [
        "residential_units",
        "commercial_units",
        "administrative_units",
        "service_units",
        "fixed_installation",
      ];
      for (const key of ALL_UNIT_LIST_KEYS) {
        const list = declarationRequest?.[key]?.data || [];
        const match = list.find(
          (u) => String(u?.buildingProfile?.renin) === String(renin),
        );
        if (match?.correct_astrick_colour) return match.correct_astrick_colour;
      }
    }
    return null;
  })();

  useEffect(() => {
    if (localMapType === "building") {
      setDeclarationRequest((prev) =>
        setRequestField(prev, "building", {
          filter_search_type_id:
            declarationRequest?.unit?.filter_search_type_id,
        }),
      );
    }
  }, []);

  const handleSearchModeChange = (id, localMapType) => {
    const modeNumber = Number(id);
    clearAllUnitErrors();

    setDeclarationRequest((prev) => {
      let updated = setRequestField(prev, localMapType, {
        // search_using: modeNumber,
        filter_search_type_id: modeNumber,
        gg_governorate_id: null,
        gg_police_station_id: null,
        gg_street_id: null,
        gg_city_id: null,
        gg_district_id: null,
        gg_mogawra_id: null,
        gg_settlement_id: null,
        manual_search_address: null,
      });
      if (localMapType === "unit") {
        updated = setRequestField(updated, "building", {
          filter_search_type_id: modeNumber,
        });
      }
      return updated;
    });
    setBuilding(null);
    // setBuilding((prev) => ({
    //   buildingProfile: prev?.buildingProfile ?? null,
    // }));

    setBuildingDate(null);
    setIsBuildingEditMode(false);
  };

  const returnFixedName = () => {
    switch (parseInt(declarationRequest?.unit?.property_type_id)) {
      case 5:
        return "service";
      case 6:
        return "hotel";
      case 7:
        return "industrial";
      case 8:
        return "production";
      case 9:
        return "petroleum";
      default:
        // fallback: if it's not one of the five, return null or throw an error
        return null;
    }
  };

  const handleDeleteAllBuildings = async () => {
    if (!staticProfileForMainBuilding) {
      setShowModal(false);
      return;
    }

    const updateSavedProfile = (prev) => {
      const next = buildMainBuildingState(staticProfileForMainBuilding)(prev);
      return {
        ...next,
        unit: {
          ...next.unit,
          buildingProfile: staticProfileForMainBuilding,
        },
      };
    };

    if (declarationRequest?.isUnitEditMode) {
      const idsToBeDeleted = getMatchingBuildings(declarationRequest)
        ?.filter((b) => b.main_building !== 1)
        .map((b) => b?.id);
      const fixedName = returnFixedName();
      const url = fixedName
        ? `api/declaration-system/declarations/${declarationRequest?.id}/units/${fixedName}/${declarationRequest?.unit?.id}/buildings`
        : null;

      try {
        await post(
          url,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Accept: "application/json",
            },
          },
          { buildings_ids: idsToBeDeleted },
        );

        if (parseInt(declarationRequest?.unit?.property_type_id) === 6) {
          setDeclarationRequest((prev) => ({
            ...prev,
            unit: {
              ...prev.unit,
              hotelUnits: [],
            },
          }));
        }
      } catch (error) {
        console.log("failed to delete ids");
      }

      setBuilding(null);
      if (emcBuildingRef.current) {
        dispatchZoomToLatLong(
          emcBuildingRef.current,
          staticProfileForMainBuilding,
        );
      }
    }

    setDeclarationRequest(updateSavedProfile);

    setBuilding((prev) => ({
      ...prev,
      buildingProfile: staticProfileForMainBuilding,
      main_building: 1,
    }));
    setShowModal(false);
  };

  const handleDeleteHotelUnits = () => {
    handleLogicOfChangingUnitBuildingProfile(
      getMatchingBuildings(declarationRequest),
      staticProfileForMainBuilding,
    );
    setShowHotelUnitsDeleteModal(false);
  };

  return (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        overlayClassName={stylesModal.overlay}
        className={stylesModal.content}
      >
        <div className="col-lg-8 text-center">
          <h5>
            {parseInt(declarationRequest?.unit?.property_type_id) === 6
              ? "جميع البيانات التي تمت إضافتها للمباني الأخرى و الوحدات التابعه لها سيتم حذفها هل ترغب في الإستمرار؟"
              : " جميع البيانات التي تمت إضافتها للمباني الأخرى سيتم حذفها هل ترغب في الإستمرار؟"}
          </h5>
          <div className="d-flex gap-4 justify-content-center">
            <button
              className="btn btn-primary px-3"
              onClick={handleDeleteAllBuildings}
            >
              نعم
            </button>
            <button
              className="btn btn-danger px-3"
              onClick={() => setShowModal(false)}
            >
              لا
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showHotelUnitsDeleteModal}
        onRequestClose={() => setShowHotelUnitsDeleteModal(false)}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        overlayClassName={stylesModal.overlay}
        className={stylesModal.content}
      >
        <div className="col-lg-8 text-center">
          <h5>
            في حالة تغيير عنوان المنشأة الرئيسية سيتم حذف جميع الوحدات التابعة
            للمنشاة هل تريد الإستمرار ؟
          </h5>
          <div className="d-flex gap-4 justify-content-center">
            <button
              className="btn btn-primary px-3"
              onClick={handleDeleteHotelUnits}
            >
              نعم
            </button>
            <button
              className="btn btn-danger px-3"
              onClick={() => setShowHotelUnitsDeleteModal(false)}
            >
              لا
            </button>
          </div>
        </div>
      </Modal>

      <div
        className={`${hasContainer && "container mt-5"} py-0 py-sm-3`}
        style={{ direction: "rtl" }}
      >
        {!declarationRequest?.viewMode &&
        !declarationRequest?.isUnitViewMode &&
        !isUnitInSameBuilding ? (
          <>
            {/* Form Fields */}
            <div className="row g-3">
              <div className="col-12">
                <div className="mb-3">
                  <div className={styles.headerRow}>
                    {localMapType === "unit" && (
                      <div className={styles.searchModesContainer}>
                        {searchUsingTypes?.map((searchItem) => (
                          <>
                            <label
                              key={searchItem?.id}
                              className={styles?.searchModeOption}
                            >
                              <input
                                type="radio"
                                name="filter_search_type_id_unit"
                                value={searchItem?.id}
                                checked={
                                  Number(activeSearchMode) ===
                                  Number(searchItem?.id)
                                }
                                onChange={() =>
                                  handleSearchModeChange(searchItem?.id, "unit")
                                }
                                disabled={isBuildingEditMode}
                              />
                              <span>{searchItem?.name}</span>
                            </label>
                            {parseInt(searchItem?.id) === 3 && (
                              <InfoPopover
                                content="في حال تعذر استخدام الخريطة يرجى تحديد العنوان يدويا مع مراعاة
              ادخال الرقم القومي العقاري إن وجد و سيتم ارسال رسالة لحضراتكم
              لتأكيد بيانات الموقع على الخريطة لاحقا"
                              >
                                <img
                                  src={infoIconSvg}
                                  alt="info"
                                  width="18"
                                  height="18"
                                />
                              </InfoPopover>
                            )}
                          </>
                        ))}
                      </div>
                    )}

                    {localMapType === "building" && (
                      <div className={styles.searchModesContainer}>
                        {searchUsingTypes?.map((searchItem) => (
                          <>
                            {" "}
                            <label
                              key={searchItem?.id}
                              className={styles?.searchModeOption}
                            >
                              <input
                                type="radio"
                                name="filter_search_type_id_building"
                                value={searchItem?.id || 1}
                                checked={
                                  Number(activeSearchMode) ===
                                  Number(searchItem?.id)
                                }
                                onChange={() =>
                                  handleSearchModeChange(
                                    searchItem?.id,
                                    "building",
                                  )
                                }
                                disabled={true}
                              />
                              <span>{searchItem?.name}</span>
                            </label>
                            {parseInt(searchItem?.id) === 3 && (
                              <InfoPopover content="في حال تعذر استخدام الخريطة يرجى تحديد العنوان يدويا مع مراعاة ادخال الرقم القومي العقاري إن وجد و سيتم ارسالة لحضراتكم لتأكيد بيانات الموقع على الخريطة لاحقا">
                                <img
                                  src={infoIconSvg}
                                  alt="info"
                                  width="18"
                                  height="18"
                                  style={{ display: "block" }}
                                />
                              </InfoPopover>
                            )}
                          </>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="row">
                  {activeSearchMode === 1 && (
                    <>
                      <div className="col-lg-4 col-sm-6">
                        <SearchSelectElement
                          mb4="mb-2"
                          labelHtmlFor="gg_governorate_id"
                          labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                          labelText="المحافظة"
                          selectClassName={`${stylesRequestData["form-select"]} form-select`}
                          selectID="gg_governorate_id"
                          inputId="gg_governorate_id_input"
                          name="gg_governorate_id"
                          options={generateOptions(govs)}
                          default="أختار المحافظة.."
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText=""
                          value={parseInt(activeUnit?.gg_governorate_id)}
                          onChange={handleChangeGov}
                          isClearable={true}
                          disabled={
                            declarationRequest?.viewMode ||
                            declarationRequest?.isUnitViewMode
                          }
                        />
                      </div>
                      <div className="col-lg-4 col-sm-6">
                        <SearchSelectElement
                          mb4="mb-2"
                          labelHtmlFor="gg_police_station_id"
                          labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                          labelText="قسم/مركز الشرطة"
                          selectClassName={`${stylesRequestData["form-select"]} form-select`}
                          selectID="gg_police_station_id"
                          inputId="police_station_id_input"
                          name="gg_police_station_id"
                          options={generateOptions(policeStations)}
                          default="أختار مركز/ قسم شرطة .."
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText=""
                          value={parseInt(activeUnit?.gg_police_station_id)}
                          onChange={handleChangePoliceStation}
                          isClearable={true}
                          disabled={
                            declarationRequest?.viewMode ||
                            declarationRequest?.isUnitViewMode
                          }
                        />
                      </div>
                      <div className="col-lg-4 col-sm-6">
                        <SearchSelectElement
                          mb4="mb-2"
                          labelHtmlFor="gg_street_id"
                          labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                          labelText="شارع"
                          selectClassName={`${stylesRequestData["form-select"]} form-select`}
                          selectID="gg_street_id"
                          inputId="gg_street_id_input"
                          name="gg_street_id"
                          options={generateOptions(streets)}
                          default="أختار الشارع .."
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText=""
                          // unit ==> declarationRequest?.unit?.gg_street_id
                          // building ==> declarationRequest?.unit?.building?.gg_street_id
                          value={parseInt(activeUnit?.gg_street_id)}
                          onChange={handleChangeStreet}
                          isClearable={true}
                          disabled={
                            declarationRequest?.viewMode ||
                            declarationRequest?.isUnitViewMode
                          }
                        />
                      </div>
                      <div className="col-lg-4 col-sm-6">
                        <SearchSelectElement
                          mb4="mb-2"
                          labelHtmlFor="gg_settlement_id"
                          labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                          labelText="التجمع السكني"
                          selectClassName={`${stylesRequestData["form-select"]} form-select`}
                          selectID="gg_settlement_id"
                          inputId="gg_settlement_id_input"
                          name="gg_settlement_id"
                          options={generateOptions(settlements)}
                          default="أختار التجمع السكني .."
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText=""
                          value={parseInt(activeUnit?.gg_settlement_id)}
                          onChange={handleChangeSettlement}
                          isClearable={true}
                          disabled={
                            declarationRequest?.viewMode ||
                            declarationRequest?.isUnitViewMode
                          }
                        />
                      </div>
                      {[5, 6, 7, 8, 9].includes(
                        parseInt(declarationRequest?.unit?.property_type_id),
                      ) &&
                        localMapType === "unit" && (
                          <div className="col-lg-4 col-sm-6">
                            <InfoPopover
                              content="في حالة تعدد المباني المكونة للمنشأة الواحدة يرجي تحديد أي من هذه المباني ليكون المبني الرئيسي للمنشأة، وسيتم تحديد رقمه القومي العقاري الذي سيجري التعامل عليه في أغراض الضرائب العقارية.
ويكون اختيار باقي العقارات التابعة والمكونة لذات المنشاة من خلال الخريطة باختيار أقرب عقار للموقع السابق (تحديده كمبني رئيسي) من العقارات التابعة للمنشآة ، ويراعي إضافة كافة المباني داخل المنشأة ."
                            >
                              <img
                                src={infoIconSvg}
                                alt="info"
                                width="24"
                                height="24"
                                style={{ display: "block", marginTop: "40px" }}
                              />
                            </InfoPopover>
                          </div>
                        )}
                    </>
                  )}

                  {activeSearchMode === 2 && (
                    <>
                      <div className="col-lg-4 col-sm-6">
                        <SearchSelectElement
                          mb4="mb-2"
                          labelHtmlFor="gg_governorate_id"
                          labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                          labelText="المحافظة"
                          selectClassName={`${stylesRequestData["form-select"]} form-select`}
                          selectID="gg_governorate_id"
                          inputId="gg_governorate_id_input"
                          name="gg_governorate_id"
                          options={generateOptions(govs)}
                          default="أختار المحافظة.."
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText=""
                          value={parseInt(activeUnit?.gg_governorate_id)}
                          onChange={handleChangeGov}
                          isClearable={true}
                          disabled={
                            declarationRequest?.viewMode ||
                            declarationRequest?.isUnitViewMode ||
                            isUnitInSameBuilding
                          }
                        />
                      </div>
                      <div className="col-lg-4 col-sm-6">
                        <SearchSelectElement
                          mb4="mb-2"
                          labelHtmlFor="gg_city_id"
                          labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                          labelText="المدينة"
                          selectClassName={`${stylesRequestData["form-select"]} form-select`}
                          selectID="المدينة"
                          inputId="gg_city_id_input"
                          name="gg_city_id"
                          options={generateOptions(cities)}
                          default="أختار المدينة.."
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText=""
                          value={parseInt(activeUnit?.gg_city_id)}
                          onChange={handleChangeCity}
                          isClearable={true}
                          disabled={
                            declarationRequest?.viewMode ||
                            declarationRequest?.isUnitViewMode
                          }
                        />
                      </div>
                      <div className="col-lg-4 col-sm-6">
                        <SearchSelectElement
                          mb4="mb-2"
                          labelHtmlFor="gg_district_id"
                          labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                          labelText="الحي/ المنطقة"
                          selectClassName={`${stylesRequestData["form-select"]} form-select`}
                          selectID="الحي/ المنطقة"
                          inputId="district_id_input"
                          name="gg_district_id"
                          options={generateOptions(districts)}
                          default="أختار الحي/ المنطقة.."
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText=""
                          value={parseInt(activeUnit?.gg_district_id)}
                          onChange={handleChangeDistrict}
                          isClearable={true}
                          disabled={
                            declarationRequest?.viewMode ||
                            declarationRequest?.isUnitViewMode
                          }
                        />
                      </div>
                      <div className="col-lg-4 col-sm-6">
                        <SearchSelectElement
                          mb4="mb-2"
                          labelHtmlFor="gg_mogawra_id"
                          labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                          labelText="المجاورة"
                          selectClassName={`${stylesRequestData["form-select"]} form-select`}
                          selectID="المجاورة"
                          inputId="gg_mogawra_id_input"
                          name="gg_mogawra_id"
                          options={generateOptions(mogawras)}
                          default="أختار المجاورة.."
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText=""
                          value={parseInt(activeUnit?.gg_mogawra_id)}
                          onChange={handleChangeMogawra}
                          isClearable={true}
                          disabled={
                            declarationRequest?.viewMode ||
                            declarationRequest?.isUnitViewMode
                          }
                        />
                      </div>
                      <div className="col-lg-4 col-sm-6">
                        <SearchSelectElement
                          mb4="mb-2"
                          labelHtmlFor="gg_settlement_id"
                          labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                          labelText="التجمع السكني"
                          selectClassName={`${stylesRequestData["form-select"]} form-select`}
                          selectID="gg_settlement_id"
                          inputId="gg_settlement_id_input"
                          name="gg_settlement_id"
                          options={generateOptions(settlements)}
                          default="أختار التجمع السكني .."
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          pText=""
                          value={parseInt(activeUnit?.gg_settlement_id)}
                          onChange={handleChangeSettlement}
                          isClearable={true}
                          disabled={
                            declarationRequest?.viewMode ||
                            declarationRequest?.isUnitViewMode
                          }
                        />
                      </div>
                      {[5, 6, 7, 8, 9].includes(
                        parseInt(declarationRequest?.unit?.property_type_id),
                      ) &&
                        localMapType === "unit" && (
                          <div className="col-lg-4 col-sm-6">
                            <InfoPopover
                              content="في حالة تعدد المباني المكونة للمنشأة الواحدة يرجي تحديد أي من هذه المباني ليكون المبني الرئيسي للمنشأة، وسيتم تحديد رقمه القومي العقاري الذي سيجري التعامل عليه في أغراض الضرائب العقارية.
ويكون اختيار باقي العقارات التابعة والمكونة لذات المنشاة من خلال الخريطة باختيار أقرب عقار للموقع السابق (تحديده كمبني رئيسي) من العقارات التابعة للمنشآة ، ويراعي إضافة كافة المباني داخل المنشأة ."
                            >
                              <img
                                src={infoIconSvg}
                                alt="info"
                                width="24"
                                height="24"
                                style={{ display: "block", marginTop: "40px" }}
                              />
                            </InfoPopover>
                          </div>
                        )}
                    </>
                  )}

                  {activeSearchMode === 3 && (
                    <>
                      {renderGovernorateAndPoliceManualFields()}

                      <div className="col-12 col-lg-4">
                        <InputElement
                          mb4="mb-4"
                          labelHtmlFor="manual_city"
                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                          labelText="المدينة"
                          inputType="text"
                          maxLength={100}
                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                          inputID="manual_city_input"
                          name="manual_city"
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          value={declarationRequest?.unit?.manual_city || ""}
                          allowedPattern={
                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9,.\-،/\s]+$/
                          }
                          onChange={handleChangeDeclerationUnit}
                          pText=""
                          disabled={isViewMode || localMapType === "building"}
                        />
                      </div>
                      <div className="col-12 col-lg-4">
                        <InputElement
                          mb4="mb-4"
                          labelHtmlFor="manual_district"
                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                          labelText="الحي/المنطقة"
                          inputType="text"
                          maxLength={100}
                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                          inputID="manual_district_input"
                          name="manual_district"
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          value={
                            declarationRequest?.unit?.manual_district || ""
                          }
                          allowedPattern={
                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9,.\-،/\s]+$/
                          }
                          onChange={handleChangeDeclerationUnit}
                          pText=""
                          disabled={isViewMode || localMapType === "building"}
                        />
                      </div>

                      <div className="col-12 col-lg-4">
                        <InputElement
                          mb4="mb-4"
                          labelHtmlFor="manual_village"
                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                          labelText="القرية"
                          inputType="text"
                          maxLength={100}
                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                          inputID="manual_village_input"
                          name="manual_village"
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          value={declarationRequest?.unit?.manual_village || ""}
                          allowedPattern={
                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9,.\-،/\s]+$/
                          }
                          onChange={handleChangeDeclerationUnit}
                          pText=""
                          disabled={isViewMode || localMapType === "building"}
                        />
                      </div>

                      <div className="col-12 col-lg-4">
                        <InputElement
                          mb4="mb-4"
                          labelHtmlFor="manual_mogawra"
                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                          labelText="المجاورة/المجموعة/المرحلة"
                          inputType="text"
                          maxLength={100}
                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                          inputID="manual_mogawra_input"
                          name="manual_mogawra"
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          value={declarationRequest?.unit?.manual_mogawra || ""}
                          allowedPattern={
                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9,.\-،/\s]+$/
                          }
                          onChange={handleChangeDeclerationUnit}
                          pText=""
                          disabled={isViewMode || localMapType === "building"}
                        />
                      </div>

                      <div className="col-12 col-lg-4">
                        <InputElement
                          mb4="mb-4"
                          labelHtmlFor="manual_street"
                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                          labelText="الشارع"
                          inputType="text"
                          maxLength={100}
                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                          inputID="manual_street_input"
                          name="manual_street"
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          value={activeUnit?.manual_street || ""}
                          allowedPattern={
                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9,.\-،/\s]+$/
                          }
                          onChange={handleChangeDeclerationUnit}
                          pText=""
                          disabled={
                            isViewMode ||
                            (localMapType === "building" &&
                              parseInt(building?.main_building) === 1)
                          }
                        />
                      </div>

                      <div className="col-12 col-lg-4">
                        <InputElement
                          mb4="mb-4"
                          labelHtmlFor="manual_settlement"
                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                          labelText="التجمع السكني"
                          inputType="text"
                          maxLength={100}
                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                          inputID="manual_settlement_input"
                          name="manual_settlement"
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          value={
                            declarationRequest?.unit?.manual_settlement || ""
                          }
                          allowedPattern={
                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9,.\-،/\s]+$/
                          }
                          onChange={handleChangeDeclerationUnit}
                          pText=""
                          disabled={isViewMode || localMapType === "building"}
                        />
                      </div>

                      {renderBuildKnownManualField()}
                      <label
                        className={`form-label ${stylesRequestData["form-label"]} mb-0 p-0  mt-2 me-2`}
                      >
                        الرقم القومي العقاري
                        <InfoPopover content="يمكن ادخال الرقم القومي العقاري من خلال الرقم المدون على ايصال الكهرباء أو الغار الطبيعي مع مراعاة إرفاق صورة من الإيصال ضمن المستندات">
                          <img
                            src={infoIconSvg}
                            alt="info"
                            width="20"
                            height="20"
                            style={{
                              display: "block",
                              marginRight: "5px",
                              marginTop: "1px",
                            }}
                          />
                        </InfoPopover>
                      </label>
                      <div className="col-12 col-lg-4">
                        <InputElement
                          mb4="mb-4"
                          labelHtmlFor="manual_renin"
                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                          labelText=""
                          inputType="text"
                          maxLength={30}
                          placeholder="الرقم القومي العقاري..."
                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                          inputID="manual_renin_input"
                          name="manual_renin"
                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                          value={activeUnit?.manual_renin || ""}
                          allowedPattern={/^[0-9\-]+$/}
                          onChange={handleChangeDeclerationUnit}
                          pText=""
                          disabled={
                            isViewMode ||
                            (localMapType === "building" &&
                              parseInt(building?.main_building) === 1)
                          }
                        />
                      </div>
                      {[5, 6, 7, 8, 9].includes(
                        parseInt(declarationRequest?.unit?.property_type_id),
                      ) &&
                        localMapType === "unit" && (
                          <div className="col-lg-4 col-sm-6">
                            <InfoPopover content="في حالة تعدد المباني المكونة للمنشأة الواحدة يرجى تحديد أي من هذه المباني ليكون المبنى الرئيسي للمنشأة مع استكمال بيانات المبنى الرئيسي في قسم وصف المباني و يراعى إضافة كافة المباني داخل المنشأة">
                              <img
                                src={infoIconSvg}
                                alt="info"
                                width="24"
                                height="24"
                                style={{ display: "block", marginTop: "40px" }}
                              />
                            </InfoPopover>
                          </div>
                        )}
                    </>
                  )}

                  {activeSearchMode !== 3 && (
                    <>
                      {!activeBuildingProfile && (
                        <>
                          {props?.unitMapError === "" ? (
                            <div
                              className="text-danger mb-0 fw-bolder text-right"
                              style={{
                                marginTop: "20px",
                                fontSize: "15px",
                              }}
                            >
                              يجب ان يتم اختيار عقار من على الخريطة لاستكمال
                              البيانات ويرجى توخي الدقة في تحديد الموقع والتأكد
                              من دقة موقع العقار المحدد على الخريطة قبل
                              الاستمرار وعند اضافة عقار جديد اتبع نفس الخطوات
                            </div>
                          ) : (
                            <div
                              className="text-danger mb-0 fw-bolder text-center"
                              style={{
                                marginTop: "70px",
                                fontSize: "15px",
                              }}
                            ></div>
                          )}
                        </>
                      )}
                      <div
                        className={` w-100 position-relative ${
                          !declarationRequest?.viewMode &&
                          !declarationRequest?.isUnitViewMode &&
                          !isUnitInSameBuilding &&
                          // !props.isMainBuilding &&
                          stylesRequestData["mb-sm-custom"]
                        } `}
                        style={{
                          height:
                            !declarationRequest?.viewMode &&
                            !declarationRequest?.isUnitViewMode &&
                            !isUnitInSameBuilding &&
                            // !props.isMainBuilding &&
                            "460px",
                          zIndex: "0",
                        }}
                      >
                        {localMapType === "unit" && (
                          <>
                            {!declarationRequest?.viewMode &&
                              !declarationRequest?.isUnitViewMode &&
                              !isUnitInSameBuilding && (
                                <>
                                  <enterprise-map-control
                                    id="map-unit"
                                    ref={emcRef}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "block",
                                    }}
                                  />

                                  {localMapType === "unit" &&
                                    !activeBuildingProfile && (
                                      <>
                                        {props?.unitMapError === "" ? (
                                          <div
                                            className="text-primary mb-0 fw-bolder text-center"
                                            style={{
                                              marginTop: "70px",
                                              fontSize: "15px",
                                            }}
                                          >
                                            لاستدعاء عنوان العقار بالرجاء الضغط
                                            مرتين على العقار المحدد. وفي حاله
                                            عدم وجود العقار علي الخريطة يرجى
                                            اختيار موقع أقرب عقار لحين تحديث
                                            بيانات الخريطة.
                                          </div>
                                        ) : (
                                          <div
                                            className="text-danger mb-0 fw-bolder text-center"
                                            style={{
                                              marginTop: "70px",
                                              fontSize: "15px",
                                            }}
                                          >
                                            {props?.unitMapError}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  {localMapType === "building" &&
                                    parseInt(building?.main_building) === 1 && (
                                      <div
                                        className="text-primary mb-0 fw-bolder text-center"
                                        style={{
                                          marginTop: "70px",
                                          fontSize: "15px",
                                          zIndex: 10,
                                        }}
                                      >
                                        برجاء استكمال بيانات المبنى الرئيسي في
                                        قسم وصف المباني
                                      </div>
                                    )}
                                </>
                              )}
                          </>
                        )}

                        {localMapType === "building" && (
                          <>
                            {!declarationRequest?.viewMode &&
                              !declarationRequest?.isUnitViewMode &&
                              !isUnitInSameBuilding && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                  }}
                                >
                                  {/* Map */}
                                  <div style={{ flex: 1 }}>
                                    <enterprise-map-control
                                      id="map-building"
                                      ref={emcBuildingRef}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        display: "block",
                                      }}
                                    />
                                  </div>

                                  {/* Messages */}
                                  {!activeBuildingProfile && (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        padding: "10px",
                                      }}
                                    >
                                      {props?.unitBuildingMapError !== "" && (
                                        <div
                                          className="text-danger mb-0 fw-bolder"
                                          style={{ fontSize: "15px" }}
                                        >
                                          {props?.unitBuildingMapError}
                                        </div>
                                      )}

                                      {parseInt(building?.main_building) !==
                                        1 && (
                                        <div
                                          className="text-primary mb-0 fw-bolder"
                                          style={{
                                            fontSize: "15px",
                                            marginTop: "5px",
                                          }}
                                        >
                                          يجب ان يتم اختيار عقار من على الخريطة
                                          لإستكمال البيانات
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                          </>
                        )}
                      </div>

                      {/* الرقم القومي العقاري - only after map profile selection */}
                      {activeBuildingProfile && (
                        <div className="d-inline-flex">
                          <div className="col-12 col-lg-6 mt-3 mt-sm-1">
                            <InputElement
                              mb4="mb-2"
                              labelHtmlFor="renin"
                              labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                              labelText="الرقم القومي العقاري"
                              inputType="text"
                              inputClassName={`${stylesRequestData["form-control"]} form-control`}
                              inputID="renin"
                              name="renin"
                              placeholder="الرقم القومي العقاري"
                              value={
                                activeUnit?.renin ||
                                activeBuildingProfile?.renin ||
                                "لم يتم تحديده بعد" ||
                                ""
                              }
                              onChange={handleChangeDeclerationUnit}
                              disabled={true}
                            />
                          </div>
                          {/* {parseInt(building?.main_building) !== 1 && ( */}
                          <div
                            className="d-inline-flex align-items-center gap-2"
                            style={{ marginRight: "10px", marginTop: "20px" }}
                          >
                            <InfoPopover content="يرجي مراجعة البيانات، وفي حالة اختلاف أسم الشارع أو رقم العقار يرجي اختيار تصحيح البيانات وكتابة عنوان العقار كما هو ثابت في مستندات الملكية .">
                              <img
                                src={infoIconSvg}
                                alt="info"
                                width="18"
                                height="18"
                                style={{ display: "block" }}
                              />
                            </InfoPopover>
                          </div>
                          {/* )}  */}
                        </div>
                      )}
                      {localMapType === "unit" ||
                      (localMapType === "building" &&
                        getMatchingBuildings(declarationRequest)?.length >
                          0) ? (
                        <>
                          {" "}
                          {activeBuildingProfile && (
                            <>
                              <div className="mt-1">
                                <label
                                  className={`form-label ${stylesRequestData["form-label"]}`}
                                >
                                  عنوان العقار
                                </label>

                                {correct_astrick_colour && (
                                  <span
                                    className={`${correct_astrick_colour} mr-1`}
                                  >
                                    *
                                  </span>
                                )}

                                <div className={styles.addressDisplay}>
                                  {buildFullAddressDisplay(
                                    activeBuildingProfile,
                                    // activeUnit,
                                  )}
                                </div>
                              </div>

                              <div className="row mt-4 g-3">
                                <div className="col-12 col-lg-6">
                                  <SearchSelectElement
                                    mb4="mb-0"
                                    labelHtmlFor="is_map_address_matching"
                                    labelClassName={` required form-label fw-bold ${stylesRequestData["form-label"]}`}
                                    labelText="هل بيانات العنوان الواردة من الخريطة مطابقة لعنوان العقار محل الإقرار؟"
                                    selectClassName={`${stylesRequestData["form-select"]} form-select`}
                                    selectID="is_map_address_matching"
                                    inputId="is_map_address_matching_input"
                                    name="is_map_address_matching"
                                    options={generateOptions(
                                      addressMatchOptions,
                                    )}
                                    default="اختر الإجابة"
                                    pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                                    pText={
                                      activeError?.is_map_address_matching ?? ""
                                    }
                                    value={activeUnit?.is_map_address_matching}
                                    onChange={handleChangeDeclerationUnit}
                                    isClearable={true}
                                    disabled={isViewMode}
                                  />
                                </div>

                                {isAddressMismatch && (
                                  <div className="col-12 col-lg-6">
                                    <SearchSelectElement
                                      mb4="mb-0"
                                      labelHtmlFor="mismatch_reason_id"
                                      labelClassName={`required form-label ${stylesRequestData["form-label"]}`}
                                      labelText="سبب عدم تطابق البيانات"
                                      selectClassName={`${stylesRequestData["form-select"]} form-select`}
                                      selectID="mismatch_reason_id"
                                      inputId="mismatch_reason_id_input"
                                      name="mismatch_reason_id"
                                      options={generateOptions(
                                        correctionReasons,
                                      )}
                                      default="أختر سبب التصحيح"
                                      pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                                      pText={activeError?.mismatch_reason_id}
                                      value={activeUnit?.mismatch_reason_id}
                                      onChange={handleChangeDeclerationUnit}
                                      isClearable={true}
                                      disabled={isViewMode}
                                    />
                                  </div>
                                )}

                                {shouldShowManualCityLookup && (
                                  <div className="col-12 col-lg-4">
                                    <InputElement
                                      mb4="mb-4"
                                      labelHtmlFor="correct_city_other"
                                      labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                                      labelText="المدينة"
                                      inputType="text"
                                      maxLength={100}
                                      inputClassName={`${stylesRequestData["form-control"]} form-control`}
                                      inputID="correct_city_other_input"
                                      name="correct_city_other"
                                      pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                                      value={
                                        activeUnit?.correct_city_other || ""
                                      }
                                      allowedPattern={
                                        /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9\u0660-\u0669\s]+$/
                                      }
                                      onChange={handleChangeDeclerationUnit}
                                      pText=""
                                      disabled={isViewMode}
                                    />
                                  </div>
                                )}

                                {isAddressMismatch &&
                                  activeUnit?.mismatch_reason_id && (
                                    <>
                                      <div className="col-12 col-lg-4">
                                        <InputElement
                                          mb4="mb-4"
                                          labelHtmlFor="correct_district_other"
                                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                                          labelText="الحي/المنطقة"
                                          inputType="text"
                                          maxLength={100}
                                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                                          inputID="correct_district_other_input"
                                          name="correct_district_other"
                                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                                          value={
                                            activeUnit?.correct_district_other ||
                                            ""
                                          }
                                          allowedPattern={
                                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9\u0660-\u0669\s]+$/
                                          }
                                          onChange={handleChangeDeclerationUnit}
                                          pText=""
                                          disabled={isViewMode}
                                        />
                                      </div>

                                      <div className="col-12 col-lg-4">
                                        <InputElement
                                          mb4="mb-4"
                                          labelHtmlFor="correct_mogawra_other"
                                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                                          labelText="المجاورة/المجموعة/المرحلة"
                                          inputType="text"
                                          maxLength={100}
                                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                                          inputID="correct_mogawra_other_input"
                                          name="correct_mogawra_other"
                                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                                          value={
                                            activeUnit?.correct_mogawra_other ||
                                            ""
                                          }
                                          allowedPattern={
                                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9\u0660-\u0669\s]+$/
                                          }
                                          onChange={handleChangeDeclerationUnit}
                                          pText=""
                                          disabled={isViewMode}
                                        />
                                      </div>

                                      <div className="col-12 col-lg-4">
                                        <InputElement
                                          mb4="mb-4"
                                          labelHtmlFor="correct_village_other"
                                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                                          labelText="القرية"
                                          inputType="text"
                                          maxLength={100}
                                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                                          inputID="correct_village_other_input"
                                          name="correct_village_other"
                                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                                          value={
                                            activeUnit?.correct_village_other ||
                                            ""
                                          }
                                          allowedPattern={
                                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9\u0660-\u0669\s]+$/
                                          }
                                          onChange={handleChangeDeclerationUnit}
                                          pText=""
                                          disabled={isViewMode}
                                        />
                                      </div>

                                      <div className="col-12 col-lg-4">
                                        <InputElement
                                          mb4="mb-4"
                                          labelHtmlFor="correct_street_other"
                                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                                          labelText="الشارع"
                                          inputType="text"
                                          maxLength={100}
                                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                                          inputID="correct_street_other_input"
                                          name="correct_street_other"
                                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                                          value={
                                            activeUnit?.correct_street_other ||
                                            ""
                                          }
                                          allowedPattern={
                                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9\-_,.\/\\\s]+$/
                                          }
                                          onChange={handleChangeDeclerationUnit}
                                          pText=""
                                          disabled={isViewMode}
                                        />
                                      </div>

                                      <div className="col-12 col-lg-4">
                                        <InputElement
                                          mb4="mb-4"
                                          labelHtmlFor="correct_settlement_other"
                                          labelClassName={` ${stylesRequestData["form-label"]} form-label`}
                                          labelText="التجمع السكني"
                                          inputType="text"
                                          maxLength={100}
                                          inputClassName={`${stylesRequestData["form-control"]} form-control`}
                                          inputID="correct_settlement_other_input"
                                          name="correct_settlement_other"
                                          pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                                          value={
                                            activeUnit?.correct_settlement_other ||
                                            ""
                                          }
                                          allowedPattern={
                                            /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9\u0660-\u0669\s]+$/
                                          }
                                          onChange={handleChangeDeclerationUnit}
                                          pText=""
                                          disabled={isViewMode}
                                        />
                                      </div>

                                      {localMapType === "building" && (
                                        <div className="col-lg-4 col-sm-6">
                                          <InputElement
                                            mb4="mb-4"
                                            labelHtmlFor="known_build_num"
                                            labelClassName={` ${stylesRequestData["form-label"]} form-label ${!validateRequired(activeUnit?.buildingProfile?.number) && "required"}`}
                                            labelText="رقم العقار/المبنى المتعارف عليه"
                                            inputType="text"
                                            maxLength={100}
                                            inputClassName={`${stylesRequestData["form-control"]} form-control`}
                                            inputID="known_build_num_input"
                                            name="known_build_num"
                                            pClassName="text-danger mb-0 mt-2 me-2 fw-bolder"
                                            value={
                                              activeUnit?.known_build_num || ""
                                            }
                                            allowedPattern={
                                              /^(?!0)[A-Za-z\u0621-\u063A\u0641-\u064A0-9\-_,.\/\\\s]+$/
                                            }
                                            onChange={
                                              handleChangeDeclerationUnit
                                            }
                                            pText={activeError?.known_build_num}
                                            disabled={isViewMode}
                                          />
                                        </div>
                                      )}
                                    </>
                                  )}
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {activeBuildingProfile && (
                            <div className="mt-1">
                              <label
                                className={`form-label ${stylesRequestData["form-label"]}`}
                              >
                                عنوان العقار
                              </label>

                              {correct_astrick_colour && (
                                <span
                                  className={`${correct_astrick_colour} mr-1`}
                                >
                                  *
                                </span>
                              )}

                              <div className={styles.addressDisplay}>
                                {buildFullAddressDisplayMainBuilding()}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="mt-1">
            <div className="col-12 col-lg-6 mt-3">
              <label
                className={`form-label ${stylesRequestData["form-label"]} mb-0 p-0`}
              >
                الرقم القومي العقاري
                {correct_astrick_colour && (
                  <span className={`${correct_astrick_colour} ms-1`}>*</span>
                )}
              </label>

              <InputElement
                mb4="mb-4"
                labelHtmlFor="renin"
                labelClassName={`form-label ${stylesRequestData["form-label"]}`}
                labelText=""
                inputType="text"
                inputClassName={`${stylesRequestData["form-control"]} form-control`}
                inputID="renin"
                name="renin"
                placeholder="الرقم القومي العقاري"
                value={
                  activeUnit?.renin ||
                  activeBuildingProfile?.renin ||
                  activeUnit?.manual_renin ||
                  "لم يتم تحديده بعد"
                }
                onChange={handleChangeDeclerationUnit}
                disabled={true}
              />
            </div>

            <label className={`form-label ${stylesRequestData["form-label"]}`}>
              عنوان العقار
            </label>

            {correct_astrick_colour && (
              <span className={`${correct_astrick_colour} mr-1`}>*</span>
            )}

            <div className={styles.addressDisplay}>
              {buildFullAddressDisplayViewMode(
                activeBuildingProfile,
                activeUnit,
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default CommonAddressMap;
